import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Loader2, Sparkles, AlertOctagon, RotateCcw, ArrowRight, Clock, History } from "lucide-react";
import { Badge } from "./Primitives";
import RecommendedActionCard from "./RecommendedActionCard";
import { formatPeso, cn } from "../lib/utils";
import { buildDashboardSnapshot } from "../lib/snapshot";
import { useRole } from "../lib/role.jsx";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SEV_BADGE = {
  critical: "critical", warning: "warning", info: "info",
  high: "critical", medium: "warning", low: "info",
};

function buildContext(anomaly) {
  if (!anomaly) return "";
  const tl = (anomaly.timeline || []).map(t => `- ${t.t}: ${t.evt}`).join("\n");
  const kpis = anomaly.relatedKpis
    ? Object.entries(anomaly.relatedKpis).map(([k, v]) => `- ${k}: ${v}`).join("\n")
    : "";
  return `ANOMALY ID: ${anomaly.id}
TYPE: ${anomaly.type} (${(anomaly.severity || "info").toUpperCase()})
TITLE: ${anomaly.title}
CONFIDENCE: ${Math.round((anomaly.confidence || 0) * 100)}%
IMPACT: ${formatPeso(anomaly.impact || 0, { compact: true })}
DETECTED: ${anomaly.detectedAt || "(unknown)"}
DETECTION METHOD: ${anomaly.method || "(unknown)"}
AFFECTED BRANCHES (${(anomaly.affectedBranches || []).length}): ${(anomaly.affectedBranches || []).join(", ") || "(none)"}
AFFECTED SKUs: ${(anomaly.affectedSkus || []).join(", ") || "(none)"}
DRIVERS: ${(anomaly.drivers || []).join("; ")}
TIMELINE:
${tl || "(no timeline)"}
RELATED KPIs:
${kpis || "(none)"}`;
}

export default function AnomalyInvestigation({ anomaly, onClose }) {
  const { role } = useRole();
  const [phase, setPhase] = useState("idle"); // idle | loading | done | error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [resolvedActions, setResolvedActions] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [prior, setPrior] = useState([]); // prior investigations from Mongo
  const [view, setView] = useState("current"); // 'current' | 'history'
  const abortRef = useRef(null);
  const sessionRef = useRef(`anom-${Math.random().toString(36).slice(2, 10)}`);

  const open = !!anomaly;

  async function fetchPrior(anomalyId) {
    try {
      const resp = await fetch(`${BACKEND}/api/anomaly/${encodeURIComponent(anomalyId)}/investigations?limit=25`);
      if (!resp.ok) return;
      const body = await resp.json();
      setPrior(Array.isArray(body.investigations) ? body.investigations : []);
    } catch {
      // best-effort
    }
  }

  async function runInvestigation(refresh = false) {
    if (!anomaly) return;
    setPhase("loading");
    setError("");
    setElapsed(0);
    if (refresh) setData(null);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const resp = await fetch(`${BACKEND}/api/copilot/investigate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionRef.current,
          role_name: role.name,
          snapshot: buildDashboardSnapshot().text,
          anomaly_context: buildContext(anomaly),
          anomaly_id: anomaly.id,
          anomaly_title: anomaly.title,
        }),
        signal: controller.signal,
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      const body = await resp.json();
      if (!body.parsed) throw new Error("AI did not return structured investigation.");
      setData(body.parsed);
      setPhase("done");
      fetchPrior(anomaly.id);
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e.message || "Investigation failed.");
        setPhase("error");
      }
    } finally {
      abortRef.current = null;
    }
  }

  // Auto-run on open + reset state
  useEffect(() => {
    if (open) {
      sessionRef.current = `anom-${Math.random().toString(36).slice(2, 10)}`;
      setResolvedActions({});
      setView("current");
      runInvestigation(true);
      fetchPrior(anomaly.id);
    } else {
      if (abortRef.current) abortRef.current.abort();
      setData(null);
      setPhase("idle");
      setError("");
      setPrior([]);
      setElapsed(0);
    }
  }, [anomaly?.id]); // eslint-disable-line

  // Elapsed timer while loading
  useEffect(() => {
    if (phase !== "loading") return;
    const id = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const mmss = useMemo(() => {
    const m = Math.floor(elapsed / 60);
    const s = elapsed % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [elapsed]);

  function loadPrior(item) {
    setData(item.parsed);
    setPhase("done");
    setResolvedActions({});
    setView("current");
  }

  return (
    <>
      <div
        className={cn("fixed inset-0 bg-black/55 backdrop-blur-sm z-40 transition-opacity",
          open ? "opacity-100" : "opacity-0 pointer-events-none")}
        onClick={onClose}
      />
      <aside
        data-testid="anomaly-drawer"
        className={cn(
          "fixed top-0 right-0 h-screen w-full sm:w-[560px] xl:w-[640px] bg-surface border-l border-border z-50 flex flex-col transition-transform duration-300",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        {anomaly && (
          <>
            {/* Header */}
            <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold inline-flex items-center gap-2 mb-1.5">
                  <Sparkles size={11} /> AI Investigation · Claude Sonnet 4.5
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge status={anomaly.severity}>{anomaly.severity}</Badge>
                  <span className="text-[10px] uppercase tracking-widest text-muted">{anomaly.type}</span>
                  <span className="text-[10px] text-subtle font-mono-tabular">{anomaly.id}</span>
                </div>
                <h2 className="font-display text-lg font-semibold leading-snug">{anomaly.title}</h2>
              </div>
              <button data-testid="close-investigation" onClick={onClose}
                      className="w-8 h-8 inline-flex items-center justify-center hover:bg-surface-2 rounded-sm shrink-0">
                <X size={16} />
              </button>
            </div>

            {/* Context strip */}
            <div className="px-5 py-3 border-b border-border bg-surface-2/50 grid grid-cols-4 gap-3">
              <Mini label="Confidence" value={`${Math.round((anomaly.confidence || 0) * 100)}%`} />
              <Mini label="Branches" value={anomaly.branches ?? (anomaly.affectedBranches || []).length} />
              <Mini label="Impact" value={formatPeso(anomaly.impact || 0, { compact: true })}
                    color={anomaly.impact < 0 ? "text-critical" : "text-success"} />
              <Mini label="Detected" value={anomaly.detectedAt?.slice(5) || "—"} />
            </div>

            {/* Tabs (only if prior investigations exist) */}
            {prior.length > 0 && (
              <div className="px-5 pt-2 border-b border-border flex items-center gap-1">
                <TabBtn active={view === "current"} onClick={() => setView("current")} testid="tab-current">
                  Current
                </TabBtn>
                <TabBtn active={view === "history"} onClick={() => setView("history")} testid="tab-history">
                  <History size={11} />
                  History
                  <span className="text-[10px] text-muted font-mono-tabular">({prior.length})</span>
                </TabBtn>
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {view === "current" && (
                <>
                  <Section title="Detection Timeline">
                    <ol className="relative border-l border-border ml-2 space-y-2.5">
                      {(anomaly.timeline || []).map((t, i) => (
                        <li key={i} className="pl-4 relative">
                          <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-primary" />
                          <div className="text-[11px] font-mono-tabular text-muted">{t.t}</div>
                          <div className="text-[13px] text-fg leading-snug">{t.evt}</div>
                        </li>
                      ))}
                    </ol>
                  </Section>

                  {(anomaly.affectedSkus?.length || anomaly.affectedBranches?.length) ? (
                    <Section title="Affected Entities">
                      {anomaly.affectedSkus?.length > 0 && (
                        <div className="mb-3">
                          <div className="kpi-label mb-1.5">SKUs</div>
                          <div className="flex flex-wrap gap-1.5">
                            {anomaly.affectedSkus.map((s, i) => (
                              <span key={i} className="text-[11px] px-2 py-0.5 bg-surface-2 border border-border rounded-sm">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {anomaly.affectedBranches?.length > 0 && (
                        <div>
                          <div className="kpi-label mb-1.5">Branches ({anomaly.affectedBranches.length})</div>
                          <div className="flex flex-wrap gap-1.5">
                            {anomaly.affectedBranches.map((b, i) => (
                              <span key={i} className="text-[11px] px-2 py-0.5 bg-surface-2 border border-border rounded-sm">{b}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </Section>
                  ) : null}

                  <div className="border-t border-border pt-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-3 flex items-center gap-2">
                      <Sparkles size={11} />
                      AI Analysis
                      {phase === "done" && (
                        <button data-testid="rerun-investigation" onClick={() => runInvestigation(true)}
                                className="ml-auto text-[10px] inline-flex items-center gap-1 text-muted hover:text-primary normal-case tracking-normal">
                          <RotateCcw size={10} /> Re-run
                        </button>
                      )}
                    </div>

                    {phase === "loading" && (
                      <div className="space-y-3" data-testid="investigation-loading">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="inline-flex items-center gap-2 text-muted">
                            <Loader2 size={14} className="animate-spin text-primary" />
                            Claude is investigating against the full 1Rotary digital twin…
                          </span>
                          <span data-testid="investigation-elapsed" className="inline-flex items-center gap-1.5 text-[11px] font-mono-tabular text-primary tabular-nums">
                            <Clock size={11} /> {mmss}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {[80, 65, 92, 55].map((w, i) => (
                            <div key={i} className="h-3 bg-surface-2 rounded-sm overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-primary/15 via-primary/30 to-primary/15 animate-shimmer"
                                   style={{ width: `${w}%`, backgroundSize: "200% 100%" }} />
                            </div>
                          ))}
                        </div>
                        <div className="text-[11px] text-subtle italic">
                          Investigations typically take 8–25 seconds. Complex multi-driver anomalies can take up to 60 seconds.
                        </div>
                      </div>
                    )}

                    {phase === "error" && (
                      <div className="border border-critical/30 bg-critical/5 text-critical p-3 rounded-sm flex items-start gap-2 text-sm" data-testid="investigation-error">
                        <AlertOctagon size={14} className="mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="font-semibold">Investigation failed</div>
                          <div className="text-xs mt-1 opacity-90">{error}</div>
                          <button onClick={() => runInvestigation(true)} className="mt-2 text-xs px-2 py-1 bg-critical text-white rounded-sm">Try again</button>
                        </div>
                      </div>
                    )}

                    {phase === "done" && data && (
                      <div className="space-y-5 animate-fade-in" data-testid="investigation-result">
                        {/* Summary */}
                        <div className="p-3 border border-primary/30 bg-primary/5 rounded-sm">
                          <div className="kpi-label text-primary mb-1.5">Executive Summary</div>
                          <p className="text-[13px] leading-relaxed text-fg">{data.summary}</p>
                        </div>

                        {data.root_causes?.length > 0 && (
                          <div>
                            <div className="kpi-label mb-2">Root Causes</div>
                            <div className="space-y-2">
                              {data.root_causes.map((rc, i) => (
                                <div key={i} className="border border-border rounded-sm p-3 flex gap-3">
                                  <span className="font-mono-tabular text-[10px] text-subtle pt-0.5 shrink-0">RC{i + 1}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-display font-semibold text-[13px]">{rc.label}</span>
                                      <Badge status={SEV_BADGE[rc.confidence] || "info"}>{rc.confidence} confidence</Badge>
                                    </div>
                                    <p className="text-[12px] text-muted leading-relaxed mt-1">{rc.detail}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {data.evidence?.length > 0 && (
                          <div>
                            <div className="kpi-label mb-2">Evidence</div>
                            <ul className="space-y-1.5">
                              {data.evidence.map((e, i) => (
                                <li key={i} className="text-[12px] text-fg/90 leading-relaxed pl-4 relative">
                                  <span className="absolute left-0 top-1.5 w-1 h-1 rounded-full bg-primary" />
                                  {e}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {data.recommended_actions?.length > 0 && (
                          <div>
                            <div className="kpi-label mb-2">Recommended Actions</div>
                            <div className="space-y-2">
                              {data.recommended_actions.map((a, i) => (
                                <RecommendedActionCard
                                  key={i}
                                  action={a}
                                  index={i}
                                  resolved={resolvedActions[i]}
                                  onApprove={() => setResolvedActions(r => ({ ...r, [i]: "approved" }))}
                                  onDefer={() => setResolvedActions(r => ({ ...r, [i]: "deferred" }))}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {data.next_decision && (
                          <div className="p-3 border-l-2 border-primary bg-surface-2 rounded-sm">
                            <div className="kpi-label text-primary mb-1 flex items-center gap-1.5">
                              <ArrowRight size={11} /> Next Decision
                            </div>
                            <p className="text-[13px] text-fg leading-relaxed">{data.next_decision}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {view === "history" && (
                <div data-testid="history-list" className="space-y-2">
                  <div className="text-[12px] text-muted">
                    Prior investigations for <span className="font-mono-tabular">{anomaly.id}</span>, persisted in MongoDB.
                  </div>
                  {prior.map(p => (
                    <button
                      key={p.id}
                      data-testid={`history-item-${p.id}`}
                      onClick={() => loadPrior(p)}
                      className="w-full text-left border border-border rounded-sm p-3 hover:border-primary/40 hover:bg-surface-2 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-mono-tabular text-muted">
                            {new Date(p.ts).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" })}
                          </div>
                          <div className="text-[13px] text-fg leading-snug mt-1 line-clamp-2">
                            {p.parsed?.summary || "(no summary)"}
                          </div>
                          <div className="text-[10px] text-subtle mt-1.5 uppercase tracking-widest">
                            {p.role_name || "executive"} · {p.parsed?.recommended_actions?.length || 0} actions
                          </div>
                        </div>
                        <ArrowRight size={14} className="text-muted shrink-0 mt-1" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-border flex items-center justify-between text-[10px] text-subtle font-mono-tabular">
              <span>session · {sessionRef.current}</span>
              <span>{role.name}</span>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="kpi-label mb-2">{title}</div>
      {children}
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div>
      <div className="kpi-label">{label}</div>
      <div className={cn("font-mono-tabular text-sm font-semibold mt-1", color || "text-fg")}>{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "h-8 px-3 text-[11px] uppercase tracking-widest font-semibold inline-flex items-center gap-1.5 border-b-2 transition-colors",
        active ? "border-primary text-primary" : "border-transparent text-muted hover:text-fg"
      )}
    >
      {children}
    </button>
  );
}
